import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Reservation, House } from '../../services/types';
import { getStatusColor } from '../../utils/reservationHelpers';
import { showSuccess } from '../../utils/notifications';
import moment from 'moment';
import { ChevronDown, ChevronRight, ChevronLeft, Calendar, X } from 'lucide-react';
import { Button, ButtonGroup } from 'react-bootstrap';
import { useUserData } from '../../hooks';
import './HouseCalendarView.css';

interface HouseCalendarViewProps {
    reservations: Reservation[];
    houses: House[];
    onSelectReservation: (reservation: Reservation) => void;
    onSelectSlot?: (slotInfo: { start: Date; end: Date; houseId: string }) => void;
}

type ViewMode = 'week' | 'month' | '2month' | '6month' | 'year';

interface CollapsedState {
    [houseId: string]: boolean;
}

const HouseCalendarView: React.FC<HouseCalendarViewProps> = ({
    reservations,
    houses,
    onSelectReservation,
    onSelectSlot,
}) => {
    const { role } = useUserData();
    const isAdminOrSuperAdmin = role === 'Admin' || role === 'Super Admin';

    const [viewMode, setViewMode] = useState<ViewMode>('month');
    const [currentDate, setCurrentDate] = useState(moment());
    const [hoveredCell, setHoveredCell] = useState<{ houseId: string; date: string } | null>(null);
    const [selectedCell, setSelectedCell] = useState<{ houseId: string; startDate: string; endDate?: string } | null>(null);
    const [isMobile, setIsMobile] = useState(false);
    const [hoveredReservationId, setHoveredReservationId] = useState<string | null>(null);
    const [collapsedHouses, setCollapsedHouses] = useState<CollapsedState>({});
    const [columnWidth, setColumnWidth] = useState<number>(200);
    const [isResizing, setIsResizing] = useState(false);

    // Refs for synchronized scrolling
    const headerScrollRef = useRef<HTMLDivElement>(null);
    const bodyScrollRef = useRef<HTMLDivElement>(null);
    const namesColumnRef = useRef<HTMLDivElement>(null);
    const isScrollingSyncRef = useRef(false);
    const isVerticalScrollingSyncRef = useRef(false);
    const resizeStartX = useRef<number>(0);
    const resizeStartWidth = useRef<number>(0);

    // Detect mobile and set initial column width
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth <= 920;
            setIsMobile(mobile);
            // Set default column width based on screen size
            if (mobile && columnWidth === 200) {
                setColumnWidth(100);
            }
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Synchronized horizontal scrolling (header and body)
    useEffect(() => {
        const headerScroll = headerScrollRef.current;
        const bodyScroll = bodyScrollRef.current;

        if (!headerScroll || !bodyScroll) return;

        const syncScroll = (source: HTMLElement, target: HTMLElement) => {
            if (isScrollingSyncRef.current) return;
            isScrollingSyncRef.current = true;
            target.scrollLeft = source.scrollLeft;
            requestAnimationFrame(() => {
                isScrollingSyncRef.current = false;
            });
        };

        const handleHeaderScroll = () => syncScroll(headerScroll, bodyScroll);
        const handleBodyScroll = () => syncScroll(bodyScroll, headerScroll);

        headerScroll.addEventListener('scroll', handleHeaderScroll);
        bodyScroll.addEventListener('scroll', handleBodyScroll);

        return () => {
            headerScroll.removeEventListener('scroll', handleHeaderScroll);
            bodyScroll.removeEventListener('scroll', handleBodyScroll);
        };
    }, []);

    // Synchronized vertical scrolling (names column and body)
    useEffect(() => {
        const namesColumn = namesColumnRef.current;
        const bodyScroll = bodyScrollRef.current;

        if (!namesColumn || !bodyScroll) return;

        const syncVerticalScroll = (source: HTMLElement, target: HTMLElement) => {
            if (isVerticalScrollingSyncRef.current) return;
            isVerticalScrollingSyncRef.current = true;
            target.scrollTop = source.scrollTop;
            requestAnimationFrame(() => {
                isVerticalScrollingSyncRef.current = false;
            });
        };

        const handleNamesScroll = () => syncVerticalScroll(namesColumn, bodyScroll);
        const handleBodyVerticalScroll = () => syncVerticalScroll(bodyScroll, namesColumn);

        namesColumn.addEventListener('scroll', handleNamesScroll);
        bodyScroll.addEventListener('scroll', handleBodyVerticalScroll);

        return () => {
            namesColumn.removeEventListener('scroll', handleNamesScroll);
            bodyScroll.removeEventListener('scroll', handleBodyVerticalScroll);
        };
    }, []);

    // Generate date range based on view mode
    const dateRange = useMemo(() => {
        const dates: moment.Moment[] = [];
        let start: moment.Moment;
        let end: moment.Moment;

        switch (viewMode) {
            case 'week':
                start = moment(currentDate).startOf('week');
                end = moment(currentDate).endOf('week');
                break;
            case 'month':
                start = moment(currentDate).startOf('month');
                end = moment(currentDate).endOf('month');
                break;
            case '2month':
                start = moment(currentDate).startOf('month');
                end = moment(currentDate).add(1, 'months').endOf('month');
                break;
            case '6month':
                start = moment(currentDate).startOf('month');
                end = moment(currentDate).add(5, 'months').endOf('month');
                break;
            case 'year':
                start = moment(currentDate).startOf('year');
                end = moment(currentDate).endOf('year');
                break;
        }

        let current = moment(start);
        while (current.isSameOrBefore(end)) {
            dates.push(moment(current));
            current.add(1, 'day');
        }

        return dates;
    }, [currentDate, viewMode]);

    // Auto-scroll to today's date on mount and when dateRange changes
    useEffect(() => {
        const bodyScroll = bodyScrollRef.current;
        const headerScroll = headerScrollRef.current;
        if (!bodyScroll || !headerScroll) return;

        // Find today's index in the date range
        const today = moment();
        const todayIndex = dateRange.findIndex(date => date.isSame(today, 'day'));

        if (todayIndex !== -1) {
            // Calculate scroll position
            const columnWidth = getColumnWidth();
            const columnWidthPx = parseInt(columnWidth);
            const scrollPosition = todayIndex * columnWidthPx;

            // Center today's date if possible
            const containerWidth = bodyScroll.clientWidth;
            const centeredPosition = Math.max(0, scrollPosition - (containerWidth / 2) + (columnWidthPx / 2));

            // Scroll after a small delay to ensure DOM is ready
            setTimeout(() => {
                bodyScroll.scrollLeft = centeredPosition;
                headerScroll.scrollLeft = centeredPosition;
            }, 100);
        }
    }, [dateRange, viewMode]);

    // ESC key handler to clear date selection
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && selectedCell) {
                setSelectedCell(null);
            }
        };

        document.addEventListener('keydown', handleEscKey);

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [selectedCell]);

    const toggleHouse = (houseId: string) => {
        setCollapsedHouses(prev => ({
            ...prev,
            [houseId]: !prev[houseId]
        }));
    };

    const handlePrevPeriod = () => {
        const today = moment();

        switch (viewMode) {
            case 'week':
                setCurrentDate(prev => moment(prev).subtract(1, 'week'));
                break;
            case 'month':
                const prevMonth = moment(currentDate).subtract(1, 'month');

                // Prevent going to months before the current month (silently)
                if (prevMonth.isBefore(today, 'month')) {
                    return;
                }

                setCurrentDate(prev => moment(prev).subtract(1, 'month'));

                // Reset scroll to beginning (day 1) after month changes
                setTimeout(() => {
                    const bodyScroll = bodyScrollRef.current;
                    const headerScroll = headerScrollRef.current;
                    if (bodyScroll && headerScroll) {
                        bodyScroll.scrollLeft = 0;
                        headerScroll.scrollLeft = 0;
                    }
                }, 50);

                // Show notification with month name
                showSuccess('', prevMonth.format('MMMM YYYY'));
                break;
            case '2month':
                setCurrentDate(prev => moment(prev).subtract(2, 'months'));
                break;
            case '6month':
                setCurrentDate(prev => moment(prev).subtract(6, 'months'));
                break;
            case 'year':
                setCurrentDate(prev => moment(prev).subtract(1, 'year'));
                break;
        }
    };

    const handleNextPeriod = () => {
        switch (viewMode) {
            case 'week':
                setCurrentDate(prev => moment(prev).add(1, 'week'));
                break;
            case 'month':
                const nextMonth = moment(currentDate).add(1, 'month');
                setCurrentDate(prev => moment(prev).add(1, 'month'));

                // Reset scroll to beginning (day 1) after month changes
                setTimeout(() => {
                    const bodyScroll = bodyScrollRef.current;
                    const headerScroll = headerScrollRef.current;
                    if (bodyScroll && headerScroll) {
                        bodyScroll.scrollLeft = 0;
                        headerScroll.scrollLeft = 0;
                    }
                }, 50);

                // Show notification with month name
                showSuccess('', nextMonth.format('MMMM YYYY'));
                break;
            case '2month':
                setCurrentDate(prev => moment(prev).add(2, 'months'));
                break;
            case '6month':
                setCurrentDate(prev => moment(prev).add(6, 'months'));
                break;
            case 'year':
                setCurrentDate(prev => moment(prev).add(1, 'year'));
                break;
        }
    };

    const handleToday = () => {
        setCurrentDate(moment());
    };

    const getReservationPosition = (reservation: Reservation, startDate: moment.Moment, endDate: moment.Moment) => {
        const checkIn = moment(reservation.check_in_date).startOf('day');
        const checkOut = moment(reservation.check_out_date).startOf('day');
        const rangeStart = dateRange[0].startOf('day');
        const rangeEnd = dateRange[dateRange.length - 1].startOf('day');

        // Check if reservation starts before or continues beyond the visible range
        const startsBeforeRange = checkIn.isBefore(rangeStart);
        const endsAfterRange = checkOut.isAfter(rangeEnd);

        // Find start position (which day index)
        let start = 0;
        for (let i = 0; i < dateRange.length; i++) {
            if (moment(dateRange[i]).isSameOrAfter(checkIn, 'day')) {
                // If reservation starts before visible range, start at 0% (full block from day 1)
                // If reservation starts on this day, start at 50% (middle of check-in day)
                start = startsBeforeRange ? i : i + 0.5;
                break;
            }
        }

        // Calculate span (how many days)
        const displayStart = moment.max(checkIn, rangeStart);
        const displayEnd = moment.min(checkOut, rangeEnd);
        let span = displayEnd.diff(displayStart, 'days');

        // Adjust span based on start and end positions
        if (startsBeforeRange && endsAfterRange) {
            // Reservation spans entire visible range (starts before, ends after)
            // Fill from start (0%) to end (100%) of visible range
            span += 1.0;
        } else if (startsBeforeRange) {
            // Reservation starts before range, ends within range
            // Fill from start (0%) to noon (50%) of checkout day
            span += 0.5;
        } else if (endsAfterRange) {
            // Reservation starts in range but continues beyond
            // Fill to end of last visible day (100%)
            span += 0.5;
        }
        // If reservation starts and ends within visible range, don't add anything
        // The span already represents noon-to-noon correctly

        return { start, span: Math.max(0.5, span) };
    };

    const getReservationsForHouse = (houseId: string): Reservation[] => {
        const rangeStart = dateRange[0];
        const rangeEnd = dateRange[dateRange.length - 1];

        return reservations.filter(reservation => {
            if (reservation.house_id !== houseId) return false;
            const checkIn = moment(reservation.check_in_date).startOf('day');
            const checkOut = moment(reservation.check_out_date).startOf('day');
            // Check if reservation overlaps with date range
            return checkIn.isSameOrBefore(rangeEnd, 'day') && checkOut.isSameOrAfter(rangeStart, 'day');
        });
    };

    const getReservationRow = (reservations: Reservation[], currentReservation: Reservation): number => {
        // Calculate which row this reservation should be in to avoid overlaps
        const currentCheckIn = moment(currentReservation.check_in_date).startOf('day');
        const currentCheckOut = moment(currentReservation.check_out_date).startOf('day');

        const rows: { checkIn: moment.Moment; checkOut: moment.Moment }[] = [];

        // Find reservations that come before this one
        const previousReservations = reservations.filter(res => {
            const resCheckIn = moment(res.check_in_date).startOf('day');
            return resCheckIn.isBefore(currentCheckIn) ||
                   (resCheckIn.isSame(currentCheckIn) && res.public_id < currentReservation.public_id);
        });

        // Try to place reservation in existing rows
        for (const prevRes of previousReservations) {
            const prevCheckIn = moment(prevRes.check_in_date).startOf('day');
            const prevCheckOut = moment(prevRes.check_out_date).startOf('day');

            let rowIndex = 0;
            let placed = false;

            // Find a row where this doesn't overlap
            // Check-out date can equal check-in date (same-day turnover)
            while (!placed && rowIndex < rows.length) {
                const row = rows[rowIndex];
                if (prevCheckOut.isSameOrBefore(row.checkIn) || prevCheckIn.isSameOrAfter(row.checkOut)) {
                    // No overlap, update row
                    if (prevCheckIn.isBefore(row.checkIn)) row.checkIn = prevCheckIn;
                    if (prevCheckOut.isAfter(row.checkOut)) row.checkOut = prevCheckOut;
                    placed = true;
                } else {
                    rowIndex++;
                }
            }

            if (!placed) {
                rows.push({ checkIn: prevCheckIn, checkOut: prevCheckOut });
            }
        }

        // Now place current reservation
        // Check-out date can equal check-in date (same-day turnover)
        let rowIndex = 0;
        while (rowIndex < rows.length) {
            const row = rows[rowIndex];
            if (currentCheckOut.isSameOrBefore(row.checkIn) || currentCheckIn.isSameOrAfter(row.checkOut)) {
                return rowIndex;
            }
            rowIndex++;
        }

        return rowIndex;
    };

    const handleCellClick = (houseId: string, date: moment.Moment) => {
        if (!onSelectSlot) return;

        const dateStr = date.format('YYYY-MM-DD');

        if (!selectedCell) {
            // First click - set start date
            setSelectedCell({ houseId, startDate: dateStr });
        } else if (selectedCell.houseId === houseId && !selectedCell.endDate) {
            // Second click on same house - set end date
            const start = moment(selectedCell.startDate);
            const end = date;

            const startDate = start.isBefore(end) ? start.toDate() : end.toDate();
            const endDate = start.isBefore(end) ? end.toDate() : start.toDate();

            onSelectSlot({ start: startDate, end: endDate, houseId });
            setSelectedCell(null);
        } else {
            // Click on different house or reset
            setSelectedCell({ houseId, startDate: dateStr });
        }
    };

    const isCellSelected = (houseId: string, date: moment.Moment): boolean => {
        if (!selectedCell || selectedCell.houseId !== houseId) return false;

        const dateStr = date.format('YYYY-MM-DD');
        const start = moment(selectedCell.startDate);
        const current = moment(dateStr);

        if (!selectedCell.endDate) {
            // Only start date selected
            return current.isSame(start, 'day');
        }

        return false;
    };

    const isCellInRange = (houseId: string, date: moment.Moment): boolean => {
        if (!selectedCell || !hoveredCell || selectedCell.houseId !== houseId) return false;
        if (hoveredCell.houseId !== houseId) return false;

        const dateStr = date.format('YYYY-MM-DD');
        const start = moment(selectedCell.startDate);
        const hover = moment(hoveredCell.date);
        const current = moment(dateStr);

        const rangeStart = start.isBefore(hover) ? start : hover;
        const rangeEnd = start.isBefore(hover) ? hover : start;

        return current.isSameOrAfter(rangeStart, 'day') && current.isSameOrBefore(rangeEnd, 'day');
    };

    const getPeriodLabel = (): string => {
        switch (viewMode) {
            case 'week':
                return `Week of ${currentDate.startOf('week').format('MMM DD, YYYY')}`;
            case 'month':
                return currentDate.format('MMMM YYYY');
            case '2month':
                return `${currentDate.format('MMM YYYY')} - ${moment(currentDate).add(1, 'months').format('MMM YYYY')}`;
            case '6month':
                return `${currentDate.format('MMM YYYY')} - ${moment(currentDate).add(5, 'months').format('MMM YYYY')}`;
            case 'year':
                return currentDate.format('YYYY');
        }
    };

    const getColumnWidth = (): string => {
        if (viewMode === 'week') return '120px';
        if (viewMode === 'month') return '60px';
        if (viewMode === '2month') return '40px';
        if (viewMode === '6month') return '25px';
        return '18px'; // year
    };

    const shouldShowDayNumber = (date: moment.Moment): boolean => {
        if (viewMode === 'week' || viewMode === 'month' || viewMode === '2month') return true;
        // For 6-month and year views, show only odd-numbered days (0, 2, 4, 6, etc.)
        if (viewMode === '6month' || viewMode === 'year') return date.date() % 2 === 0;
        return false;
    };

    const renderDateHeaders = () => {
        const columnWidth = getColumnWidth();

        if (viewMode === 'week' || viewMode === 'month' || viewMode === '2month') {
            // Show all days with detailed headers
            return (
                <div className="house-calendar-dates-header-scroll" ref={headerScrollRef}>
                    <div className="house-calendar-dates-header">
                        {dateRange.map((date, index) => {
                            const isToday = date.isSame(moment(), 'day');
                            const isWeekend = date.day() === 0 || date.day() === 6;

                            return (
                                <div
                                    key={index}
                                    className={`house-calendar-date-header ${isToday ? 'today' : ''} ${isWeekend ? 'weekend' : ''}`}
                                    style={{ minWidth: columnWidth, maxWidth: columnWidth }}
                                >
                                    <div className="date-header-day">{date.format('ddd')}</div>
                                    <div className="date-header-date">{date.format('DD')}</div>
                                    {viewMode === 'month' && (
                                        <div className="date-header-month">{date.format('MMM')}</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        } else {
            // 6-month and year view - show month headers + compact dates
            const monthHeaders: { month: string; count: number }[] = [];
            let currentMonth = '';
            let count = 0;

            dateRange.forEach(date => {
                const monthLabel = date.format('MMM YYYY');
                if (monthLabel !== currentMonth) {
                    if (currentMonth) {
                        monthHeaders.push({ month: currentMonth, count });
                    }
                    currentMonth = monthLabel;
                    count = 1;
                } else {
                    count++;
                }
            });
            if (currentMonth) {
                monthHeaders.push({ month: currentMonth, count });
            }

            return (
                <div className="house-calendar-dates-header-scroll" ref={headerScrollRef}>
                    <div className="house-calendar-dates-header-compact">
                        <div className="house-calendar-month-headers">
                            {monthHeaders.map((header, index) => (
                                <div
                                    key={index}
                                    className="house-calendar-month-header"
                                    style={{
                                        minWidth: `calc(${header.count} * ${columnWidth})`,
                                        maxWidth: `calc(${header.count} * ${columnWidth})`
                                    }}
                                >
                                    {header.month}
                                </div>
                            ))}
                        </div>
                        <div className="house-calendar-day-headers">
                            {dateRange.map((date, index) => {
                                const isToday = date.isSame(moment(), 'day');
                                const showDay = shouldShowDayNumber(date);

                                return (
                                    <div
                                        key={index}
                                        className={`house-calendar-date-header-compact ${isToday ? 'today' : ''}`}
                                        style={{ minWidth: columnWidth, maxWidth: columnWidth }}
                                    >
                                        {showDay && <div className="date-header-date-compact">{date.format('D')}</div>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            );
        }
    };

    const renderHouseRow = (house: House) => {
        const isCollapsed = collapsedHouses[house.public_id];
        const columnWidth = getColumnWidth();
        const houseReservations = getReservationsForHouse(house.public_id);
        const isSelectedHouse = selectedCell?.houseId === house.public_id;

        return (
            <div key={house.public_id} className={`house-calendar-house-row-dates ${isCollapsed ? 'collapsed' : ''} ${isSelectedHouse ? 'selected-house-row' : ''}`}>
                {!isCollapsed && (
                    <div className="house-calendar-dates-wrapper">
                        <div className="house-calendar-dates-container">
                            {/* Background grid for dates */}
                            <div className="house-calendar-dates-grid">
                                {dateRange.map((date, index) => {
                                    const isToday = date.isSame(moment(), 'day');
                                    const isPast = date.isBefore(moment(), 'day');
                                    const isWeekend = date.day() === 0 || date.day() === 6;
                                    const isSelected = isCellSelected(house.public_id, date);
                                    const isInRange = isCellInRange(house.public_id, date);

                                    return (
                                        <div
                                            key={index}
                                            className={`house-calendar-date-cell
                                                ${isToday ? 'today' : ''}
                                                ${isPast ? 'past' : ''}
                                                ${isWeekend ? 'weekend' : ''}
                                                ${isSelected ? 'selected' : ''}
                                                ${isInRange ? 'in-range' : ''}
                                            `}
                                            style={{ minWidth: columnWidth, maxWidth: columnWidth }}
                                            onClick={() => !isPast && handleCellClick(house.public_id, date)}
                                            onMouseEnter={() => setHoveredCell({ houseId: house.public_id, date: date.format('YYYY-MM-DD') })}
                                            onMouseLeave={() => setHoveredCell(null)}
                                        >
                                        </div>
                                    );
                                })}

                                {/* Reservation bars overlay on top of grid */}
                                <div className="house-calendar-reservations-overlay">
                                    {houseReservations.map((reservation) => {
                                        const { start, span } = getReservationPosition(
                                            reservation,
                                            dateRange[0],
                                            dateRange[dateRange.length - 1]
                                        );
                                        const colors = getStatusColor(reservation.reservation_state);

                                        // Admin/Super Admin: use house color, Normal users: use status colors
                                        const backgroundColor = isAdminOrSuperAdmin
                                            ? (house.color || colors.bg)
                                            : colors.bg;
                                        const borderColor = isAdminOrSuperAdmin
                                            ? (house.color || colors.border)
                                            : colors.border;
                                        const textColor = isAdminOrSuperAdmin ? '#ffffff' : colors.text;

                                        // Bold for confirmed, normal for pending
                                        const fontWeight = isAdminOrSuperAdmin
                                            ? (reservation.reservation_state === 'confirmed' ? '700' : '400')
                                            : '400';

                                        const leftPosition = `calc(${start} * ${columnWidth})`;
                                        const widthCalc = `calc(${span} * ${columnWidth})`;

                                        const isHovered = hoveredReservationId === reservation.public_id;

                                        return (
                                            <div
                                                key={reservation.public_id}
                                                className={`reservation-bar-house ${isHovered ? 'reservation-bar-hovered' : ''}`}
                                                style={{
                                                    backgroundColor: backgroundColor,
                                                    borderLeft: `4px solid ${borderColor}`,
                                                    color: textColor,
                                                    left: leftPosition,
                                                    width: widthCalc,
                                                    fontWeight: fontWeight,
                                                }}
                                                onMouseEnter={() => setHoveredReservationId(reservation.public_id)}
                                                onMouseLeave={() => setHoveredReservationId(null)}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onSelectReservation(reservation);
                                                }}
                                                title={`${reservation.name_first_guess || 'Guest'} - ${moment(reservation.check_in_date).format('MMM DD')} to ${moment(reservation.check_out_date).format('MMM DD')}`}
                                            >
                                                <span className="reservation-bar-content">
                                                    <span className="reservation-guest-name">
                                                        {reservation.name_first_guess || 'Guest'}
                                                    </span>
                                                    <span className="reservation-dates">
                                                        {moment(reservation.check_in_date).format('MMM DD')} - {moment(reservation.check_out_date).format('MMM DD')}
                                                    </span>
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Resize handlers
    const handleResizeStart = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        setIsResizing(true);
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        resizeStartX.current = clientX;
        resizeStartWidth.current = columnWidth;
    };

    const handleResizeMove = (e: MouseEvent | TouchEvent) => {
        if (!isResizing) return;
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const delta = clientX - resizeStartX.current;
        const newWidth = Math.max(80, Math.min(400, resizeStartWidth.current + delta));
        setColumnWidth(newWidth);
    };

    const handleResizeEnd = () => {
        setIsResizing(false);
    };

    // Add resize event listeners
    useEffect(() => {
        if (isResizing) {
            const handleMove = (e: MouseEvent | TouchEvent) => handleResizeMove(e);
            const handleEnd = () => handleResizeEnd();

            document.addEventListener('mousemove', handleMove);
            document.addEventListener('mouseup', handleEnd);
            document.addEventListener('touchmove', handleMove);
            document.addEventListener('touchend', handleEnd);

            return () => {
                document.removeEventListener('mousemove', handleMove);
                document.removeEventListener('mouseup', handleEnd);
                document.removeEventListener('touchmove', handleMove);
                document.removeEventListener('touchend', handleEnd);
            };
        }
    }, [isResizing]);

    return (
        <div className="house-calendar-container" style={{ position: 'relative', userSelect: isResizing ? 'none' : 'auto', cursor: isResizing ? 'col-resize' : 'auto' }}>
            {/* Toolbar */}
            <div className="house-calendar-toolbar">
                <div className="toolbar-row-top">
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handlePrevPeriod}
                        className="nav-arrow-btn"
                    >
                        <ChevronLeft size={18} />
                    </Button>

                    <div className="toolbar-center">
                        <div className="toolbar-period-label">
                            <Calendar size={16} className="me-2" />
                            {getPeriodLabel()}
                        </div>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleToday}
                            className="today-btn"
                        >
                            Today
                        </Button>
                    </div>

                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleNextPeriod}
                        className="nav-arrow-btn"
                    >
                        <ChevronRight size={18} />
                    </Button>
                </div>

                <ButtonGroup size="sm">
                    <Button
                        variant={viewMode === 'week' ? 'primary' : 'outline-primary'}
                        onClick={() => setViewMode('week')}
                    >
                        Week
                    </Button>
                    <Button
                        variant={viewMode === 'month' ? 'primary' : 'outline-primary'}
                        onClick={() => setViewMode('month')}
                    >
                        Month
                    </Button>
                    <Button
                        variant={viewMode === '2month' ? 'primary' : 'outline-primary'}
                        onClick={() => setViewMode('2month')}
                    >
                        2 Months
                    </Button>
                    <Button
                        variant={viewMode === '6month' ? 'primary' : 'outline-primary'}
                        onClick={() => setViewMode('6month')}
                    >
                        6 Months
                    </Button>
                    <Button
                        variant={viewMode === 'year' ? 'primary' : 'outline-primary'}
                        onClick={() => setViewMode('year')}
                    >
                        Year
                    </Button>
                </ButtonGroup>
            </div>

            {/* Selection Info */}
            {selectedCell && !selectedCell.endDate && (
                <div className="alert alert-info mb-3 d-flex justify-content-between align-items-center">
                    <div>
                        <strong>Check-in:</strong> {moment(selectedCell.startDate).format('MMM DD, YYYY')}
                        <span className="ms-3 text-muted">← Now click check-out date for the same property</span>
                    </div>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setSelectedCell(null)}
                        style={{ minWidth: 'auto' }}
                        title="Clear selection (or press ESC)"
                    >
                        <X size={20} />
                    </Button>
                </div>
            )}

            {/* Calendar Grid - Split into fixed left and scrollable right */}
            <div className={`house-calendar-grid ${viewMode}`}>
                {/* Header Row */}
                <div className="house-calendar-header-row">
                    <div
                        className="house-calendar-house-header"
                        style={{
                            minWidth: `${columnWidth}px`,
                            maxWidth: `${columnWidth}px`,
                            position: 'relative'
                        }}
                    >
                        <span>Property</span>
                        {/* Resize Handle */}
                        <div
                            className={`column-resize-handle ${isResizing ? 'resizing' : ''}`}
                            onMouseDown={handleResizeStart}
                            onTouchStart={handleResizeStart}
                            title="Drag to resize column"
                        >
                            <div style={{
                                position: 'absolute',
                                right: '3px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: '2px',
                                height: '20px',
                                backgroundColor: '#adb5bd',
                                borderRadius: '1px'
                            }} />
                        </div>
                    </div>
                    {renderDateHeaders()}
                </div>

                {/* Body with two columns */}
                <div className="house-calendar-content-wrapper">
                    {/* Left: Fixed house names */}
                    <div
                        className="house-calendar-names-column"
                        ref={namesColumnRef}
                        style={{
                            minWidth: `${columnWidth}px`,
                            maxWidth: `${columnWidth}px`
                        }}
                    >
                        {houses.map(house => {
                            const isCollapsed = collapsedHouses[house.public_id];
                            const houseReservations = getReservationsForHouse(house.public_id);
                            const isSelectedHouse = selectedCell?.houseId === house.public_id;

                            return (
                                <div
                                    key={house.public_id}
                                    className={`house-calendar-house-name-fixed ${isCollapsed ? 'collapsed' : ''} ${isSelectedHouse ? 'selected-house' : ''}`}
                                    onClick={() => toggleHouse(house.public_id)}
                                    style={{ backgroundColor: '#ffffff' }}
                                >
                                    <div className="house-name-content">
                                        {isCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                                        <span className="house-name-text" title={house.name || house.address}>
                                            {house.name || house.address}
                                        </span>
                                        <span className="house-reservation-count">
                                            {houseReservations.length}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Right: Scrollable dates and reservations */}
                    <div className="house-calendar-body" ref={bodyScrollRef}>
                        {houses.map(house => renderHouseRow(house))}
                    </div>
                </div>
            </div>

            {/* Legend */}
            <div className="house-calendar-legend">
                {/* Default legend - only for non-admin users */}
                {!isAdminOrSuperAdmin && (
                    <>
                        <div className="legend-item">
                            <div className="legend-color available"></div>
                            <span>Available</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-color confirmed"></div>
                            <span>Confirmed</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-color pending"></div>
                            <span>Pending</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-color today-marker"></div>
                            <span>Today</span>
                        </div>
                    </>
                )}

                {/* House colors - only for Admin/Super Admin */}
                {isAdminOrSuperAdmin && houses.length > 0 && (
                    <>
                        {houses.map((house) => (
                            <div key={house.public_id} className="legend-item">
                                <div
                                    className="legend-color house-color"
                                    style={{
                                        backgroundColor: house.color || '#339af0',
                                        borderColor: house.color || '#339af0'
                                    }}
                                ></div>
                                <span className="legend-house-name">{house.name || house.address}</span>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
};

export default HouseCalendarView;